import { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { CORE_SERVICE_URLS } from '~/Constants/Urls.constants';
import { requestReactQuery } from '~/Helpers/ReactQuery.helper';
import { routeReplacer } from '~/Helpers/String.helper';
import { useProvider } from '~/Hooks';
import { IErrorResponse } from '~/Models/DTO/Error.models';

export interface ExportStatusResponse {
  status: 'Pending' | 'InProgress' | 'Success' | 'Fail';
  failureMessage: string | null;
  downloadLink: string | null;
}

export const useRequestStartScheduleExport = () => {
  const { currentProvider } = useProvider();
  const result = useMutation(() =>
    requestReactQuery<string>({
      url: routeReplacer(CORE_SERVICE_URLS.EXPORT_SCHEDULE, {
        domain: currentProvider?.domain || '',
      }),
      data: {},
      config: { method: 'POST' },
    }),
  );
  return result;
};

export const useRequestCheckScheduleStatus = () => {
  const { currentProvider } = useProvider();
  const [status, setStatus] = useState<ExportStatusResponse | null>(null);
  const [exportUuid, setExportUuid] = useState<string>('');
  const startExport = async () => {
    const response = await requestReactQuery<ExportStatusResponse>({
      url: routeReplacer(CORE_SERVICE_URLS.EXPORT_SCHEDULE_STATUS, {
        domain: currentProvider?.domain || '',
        exportUuid: exportUuid || '',
      }),
      config: { method: 'GET' },
    });
    setStatus(response);
    if (response?.status !== 'Success') {
      throw response;
    }
    return response;
  };
  const { refetch } = useQuery<ExportStatusResponse, IErrorResponse>(
    [CORE_SERVICE_URLS.EXPORT_SCHEDULE_STATUS, exportUuid, currentProvider?.domain],
    startExport,
    {
      retry: (failureCount: number, error: any) => {
        //Stop retrying if the request was one of
        if (['Fail', 'Success'].includes(error?.status)) {
          return false;
        }
        return failureCount < 20; // Retry up to 10 times
      },
      retryDelay: 5000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      enabled: !!exportUuid?.length,
    },
  );

  useEffect(() => {
    const fetchStatus = async () => {
      await refetch({ queryKey: exportUuid });
    };
    if (!!exportUuid?.length) {
      fetchStatus();
    }
  }, [exportUuid?.length]);

  return {
    refetch: (uuid: string) => setExportUuid(uuid),
    data: status,
  };
};
